/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react'

import { mpfRListsHttp } from '../../../assets/ts/api/mpf-r-lists-http'
import { formatUTCDateToLocal } from '../../../assets/ts/date-and-time/date-and-time'
import {
  convertRoleToAction,
  defaultToEmptyString,
  formatRoleName,
  formatStatusName,
  getRoleFromStatusDesc,
} from '../../../assets/ts/formatting/formatting'
import { ensureNoNullValues } from '../../../assets/ts/general/general'
import useInquiryHistory from '../../../hooks/use-inquiry-history'
import {
  ACTIONS,
  INQUIRY_STATUS_IDS,
  SERVICING_BY,
  USER_ROLES,
  nodeStatusClasses,
  nonWaitingStatuses,
  waitingActions,
} from '../../../utils/constants/global-constants'
import './progress.css'

type Status = {
  action: string
  active?: boolean // Optional if not always present
  date: string // Adjust according to your actual data type
  displayName: string
  displaySection: string
  previousSection?: string // Optional if not always present
  role?: string // Optional if not always present
  submitter?: string // Optional if not always present
}

type GroupedSections = {
  active?: boolean // Optional for active state
  date?: any
  displaySection: string
  index?: number // Optional if needed for tracking
  section: Status[]
}

type ActiveStateSections = GroupedSections // Adjust if needed
type ProgressBarProps = {
  ItemGUID: string
  inquiryData: {
    AssignedTo?: string
    AssignedToEmail?: string
    AssignedToID?: string
    Customer?: {
      properties?: {
        firstName?: string
        lastName?: string
      }
      userID?: string
    }
    CustomerActionRequired?: boolean
    ID?: string
    InquiryGUID?: string
    InquiryID?: string
    ItemCreatedBy?: {
      properties?: {
        firstName?: string
        lastName?: string
      }
      userID?: string
    }
    PendingActionOutside?: boolean
    Section?: string
    ServicingBy?: string
    Status?: string
    StatusCode?: string
    StatusID?: string
    previousServicingBy?: null | string
  }
  refresh: boolean
  setCurrentAction: (action: string) => void
  setIsLoading: (isLoading: boolean) => void
}

const ProgressBar = ({
  ItemGUID,
  inquiryData,
  refresh,
  setCurrentAction,
  setIsLoading,
}: ProgressBarProps) => {
  const { HistoryData, HistoryDataIsLoading, refetch } = useInquiryHistory({
    InquiryGUID: ItemGUID,
  })
  const [groupedHistoryItems, setGroupedHistoryItems] = useState<GroupedSections[]>([])

  /**
   * Used to get the displayName for status.
   * This is needed because for some action buttons have displayName as a object and others as a string.
   * That causes the page to crash so this ensures displayname is always a string.
   *
   * @param name - The input that may be a string, null, undefined, or an object with properties.
   * @returns The display name as a string.
   */
  const displayNameForStatusHistory = (
    name: { properties?: { firstName?: string; lastName?: string } } | null | string | undefined
  ): string => {
    if (typeof name === 'string') {
      return name
    }
    if (name === undefined || name === null) {
      return ''
    }
    if (typeof name === 'object' && name.properties) {
      const { firstName, lastName } = name.properties
      return `${firstName ?? ''} ${lastName ?? ''}`.trim()
    }
    return ''
  }

  async function displayInquiryLifeCycle(historyData) {
    const userDetails = mpfRListsHttp().me()
    const loginUserId: string = (await userDetails).userId
    const statusHistoryData: any = [...historyData]
    if (historyData.length > 1) {
      const firstItem = historyData[0]
      if (inquiryData?.CustomerActionRequired || inquiryData?.PendingActionOutside) {
        const actionForHistoryItem = inquiryData?.CustomerActionRequired
          ? ACTIONS.CustomerActionRequired
          : ACTIONS.PendingActionOutsideCP
        const roleForHistoryItems = inquiryData?.CustomerActionRequired
          ? USER_ROLES.Customer
          : inquiryData?.StatusCode
          ? getRoleFromStatusID(inquiryData.StatusCode, false)
          : ''

        statusHistoryData.unshift({ ...firstItem,
          ActionCode: actionForHistoryItem,
          UserRole: roleForHistoryItems,
        })
      } else {
        // if the last action was a response then we add a history item to simulate Waiting... on the current assigned user

        if (waitingActions.includes(firstItem.ActionCode)) {
          statusHistoryData.unshift({ ...firstItem })
          // if the last history item was Change section then we add a history item to simulate Waiting... on the new section
        }
      }
    }
    const statusHistoryArray: any = []
    statusHistoryData.forEach((item, i) => {
      const waitingItem = i === 0 && waitingActions.includes(item.ActionCode)
      let action
      const servicingBy = item.ServicingBy
      const previousServicingBy = item.PreviousServicingBy
      const date = formatUTCDateToLocal(item.ItemCreated)
      let displaySection
      let previousSection
      let submitter
      let role
      let displayName =
        item.ItemCreatedBy?.properties?.firstName + ' ' + item?.ItemCreatedBy?.properties?.lastName

      const active =
        waitingItem
          ? nodeStatusClasses.Inactive
          : nodeStatusClasses.Active

      if (waitingItem) {
        action = 'WAITING'
        displaySection = item.StatusCode.includes('CSS') ? 'CSS' : item['Section/MetadataValue']

        switch (item.ActionCode) {
          case ACTIONS.CustomerActionRequired:
            displayName = inquiryData?.Customer?.properties.defaultDisplay
            role = 'Customer'
            break
          case ACTIONS.Elevated:
            displayName = ''
            role = getRoleFromStatusID(item.StatusCode, false)
            break
          case ACTIONS.Self:
            role = getRoleFromStatusID(item.StatusCode, false)
            break
          case ACTIONS.OutsideActionComplete:
            role = getRoleFromStatusID(item.StatusCode, false)
            break
          case ACTIONS.PendingActionOutsideCP:
            displayName = item.AssignedTo?.properties.defaultDisplay
            role = getRoleFromStatusID(item.StatusCode, false)
            break
          case ACTIONS.CancelResponseRequired:
            role = 'Technician'
            displayName = ''
            displaySection = item['PreviousSection/MetadataValue']
            break
          case ACTIONS.OtherSection:
            displayName = ''
            role = 'Technician'
            break
          case ACTIONS.CustomerActionComplete:
            role = getRoleFromStatusID(item.StatusCode, false)
            displayName = item.AssignedTo?.properties.defaultDisplay
            break
          case ACTIONS.Responded:
            role = getRoleFromStatusID(item.StatusCode, false)
            displayName = item.AssignedTo?.properties.defaultDisplay
            break
          case ACTIONS.Other:
            role = getRoleFromStatusID(item.StatusCode, false)
            displayName = item.AssignedTo?.properties.defaultDisplay
            break
          default:
            break
        }
      }

      if (!waitingItem) {
        // Display Name Block
        if (item.ActionCode === 'ASSIGN_TO_SECTION' || // Change Section
            (item.ActionCode === 'RESPOND' && item['PreviousSection/MetadataValue'] && // Respond to Response Required Inquiry
            item['Section/MetadataValue'] !== item['PreviousSection/MetadataValue'])) {
          displaySection = item['PreviousSection/MetadataValue']
        } else if ((item.ActionCode ==='RESPOND' && item['PreviousSection/MetadataValue'] === null)) { // Respond to Elevated Inquiry
          displaySection = item['Section/MetadataValue']
        } else if (item.ServicingBy === 'MPF' && item.PreviousServicingBy === 'MPF') {
          displaySection = item['Section/MetadataValue']
        } else if (item.ServicingBy === 'MPF' && item.PreviousServicingBy === 'CSS') {
          displaySection = 'CSS'
        } else if (item.ServicingBy === 'CSS' && item.PreviousServicingBy === 'MPF') {
          displaySection = item['Section/MetadataValue']
        } else if (item.ServicingBy === 'CSS' && item.PreviousServicingBy === null) {
          displaySection = 'CSS'
        } else if (item.ServicingBy === 'MPF' && item.PreviousServicingBy === null) {
          displaySection = item['Section/MetadataValue']
        }

        //Display Name Block
        if (item.ActionCode == ACTIONS.Submitted && !item.PreviousStatusCode) {
          displayName = inquiryData?.Customer?.userID
            ? inquiryData?.Customer?.properties?.firstName +
              ' ' +
              inquiryData?.Customer?.properties?.lastName
            : displayName
          submitter =
            inquiryData?.Customer?.userID != inquiryData?.ItemCreatedBy?.userID
              ? item.ItemCreatedBy?.properties?.firstName +
                ' ' +
                item?.ItemCreatedBy?.properties?.lastName
              : ''
        } else if (
          (loginUserId != inquiryData?.Customer || item.UserRole == USER_ROLES.Customer) &&
          (item.ActionCode != ACTIONS.CustomerActionRequired ||
            (item.ActionCode == ACTIONS.CustomerActionRequired &&
              item.UserRole != USER_ROLES.Customer))
        ) {
          displayName = item.Author ? item.Author : displayName
          submitter = ''
        } else {
          displayName = ''
          submitter = ''
        }

        // Action Block
        if (item.ActionCode == ACTIONS.Responded) {
          action = 'RESPONDED'
        } else if (item.ActionCode == ACTIONS.AssignedToOtherSection) {
          action = 'ASSIGNED TO OTHER SECTION'
        } else if (item.ActionCode == ACTIONS.CancelResponseRequired) {
          action = 'CANCELED_RESPONSE_REQUESTED'
        } else {
          action = item.ActionCode
        }

        //Role Block
        if (item.ActionCode == ACTIONS.Assigned) {
          role = removeSectionFromRole(item.UserRole)
        } else if (item.UserRole == ACTIONS.PendingActionOutsideCP) {
          role = ACTIONS.PendingActionOutsideCP
        } else {
          if (
            item.ActionCode == ACTIONS.CustomerActionComplete &&
            item.UserRole != USER_ROLES.Customer
          ) {
            role = item.StatusCode ? getRoleFromStatusID(item.StatusCode, false) : ''
          } else if (item.ActionCode == ACTIONS.CancelResponseRequired) {
            role = removeSectionFromRole(item.UserRole)
          } else if (!role) {
            role = removeSectionFromRole(item.UserRole)
          }
  
          role = role == 'Submitter' ? USER_ROLES.Customer : role
          if (role == 'Administration' && loginUserId == inquiryData?.Customer) {
            role = USER_ROLES.Customer
          }
        }
      }
      const statusObject = {
        action,
        active,
        date,
        displayName,
        displaySection,
        previousSection,
        previousServicingBy,
        role,
        servicingBy,
        submitter,
      }
      statusHistoryArray.push(statusObject)
    })
    const groupedHistoryData = combinedSectionsByDisplaySection(statusHistoryArray)
    setGroupedHistoryItems(groupedHistoryData)
  }

  // Combine sections by displaySection
  const combinedSectionsByDisplaySection = (data: GroupedSections[]): GroupedSections[] => {
    const result: any = []
    let currentGroup: GroupedSections[] = []
    let currentDisplaySection = ''

    for (const item of data) {
      if (item?.displaySection === currentDisplaySection) {
        currentGroup.push(item)
      } else {
        if (currentGroup.length > 0) {
          result.push(currentGroup)
        }
        currentGroup = [item]
        currentDisplaySection = item.displaySection
      }
    }

    if (currentGroup.length > 0) {
      result.push(currentGroup)
    }
    return result
  }
  function removeSectionFromRole(role) {
    const splitRole = role.split(' ')
    const roleWithoutSection = splitRole[splitRole.length - 1]
    if (role.includes(USER_ROLES.SectionChief)) return USER_ROLES.SectionChief
    if (role.includes(USER_ROLES.Leadership)) return SERVICING_BY.MPF + ' ' + roleWithoutSection
    else return roleWithoutSection
  }
  function getRoleFromStatusID(statusId: string, showSection: boolean): string {
    let role
    if (showSection) {
      role = statusId.replaceAll('-', ' ')
    } else {
      const splitRole = statusId.split('-')
      if (splitRole[1]) {
        role = splitRole[1] === USER_ROLES.CSS ? USER_ROLES.CSS : formatRoleName(splitRole[1])
      } else {
        role = splitRole[0]
      }
    }
    return role === 'MPF_LEADERSHIP' ? 'MPF Leadership' : role
  }

  function getDisplaySection(displaySection: GroupedSections[]): string {
    return defaultToEmptyString(displaySection[0]?.displaySection)
  }

  useEffect(() => {
    displayInquiryLifeCycle(HistoryData)
  }, [HistoryData, inquiryData])

  useEffect(() => {
    const firstGroup = HistoryData?.[0]
    const firstAction = firstGroup?.ActionCode
    if (firstAction) {
      setCurrentAction(firstAction)
    }
  }, [HistoryData])

  useEffect(() => {
    setIsLoading(HistoryDataIsLoading)
  }, [HistoryDataIsLoading])

  useEffect(() => {
    if (refresh) {
      // Call refetch when refresh is changed
      refetch()
    }
  }, [refresh])

  return (
    <>
      <div className='card-header border-bottom1 d-flex align-items-center'>
        <h2
          aria-label='Inquiry Status'
          className='title-color20 poppins-semibold font-14'
          tabIndex={0}
        >
          Inquiry Status
        </h2>
      </div>
      <div className='card-body '>
        <div className='ms-auto'>
          <ul className='list-type-none progress v-steps w-100'>
            {groupedHistoryItems.map((progressItems, key) => (
              <li className='px-2 px-xl-3' key={key}>
                <div className='progress-steps'>
                  {progressItems[0].active === 'inactive' || HistoryData.length === 1 ? (
                    <div className='circle'> </div>
                  ) : (
                    <div className='circle outeractive'> </div>
                  )}
                  <span className='ps-1'>{getDisplaySection(progressItems)}</span>

                  <div className='progress-title pb-1 d-flex flex-column w-100 ps-4'>
                    <div className='border-bottom1 pb-1 w-100'></div>
                    <ul className='list-type-none progress v-steps w-100 ps-2'>
                      {groupedHistoryItems[key].map((progressInnerItem, key) => {
                        return (
                          <li className='px-2 px-xl-3' key={key * 100}>
                            <div className='progress-steps'>
                              {progressInnerItem?.active === 'inactive' ? (
                                <div className='circle'> </div>
                              ) : (
                                <div className='circle outeractive'> </div>
                              )}
                              <div
                                aria-live='polite'
                                className='progress-title pb-1 d-flex flex-column ps-4'
                                tabIndex={0}
                              >
                                <span className='ttile-color poppins-semibold font-13'>
                                  {defaultToEmptyString(progressInnerItem.role)}
                                </span>
                                <span className='title-color23 sourcesanspro font-12 '>
                                  {defaultToEmptyString(
                                    displayNameForStatusHistory(progressInnerItem.displayName)
                                  )}
                                </span>
                                <span className='title-color23 sourcesanspro font-12 '>
                                  {defaultToEmptyString(progressInnerItem?.date)}
                                </span>
                                <span className='border-primary maxfitcontent color-primary mt-1 px-2 py-1 border-radius sourcesanspro font-13 text-uppercase breakword'>
                                  {defaultToEmptyString(
                                    convertRoleToAction(progressInnerItem?.action)
                                  )}
                                </span>
                                {progressInnerItem?.submitter && (
                                  <span className='title-color23 sourcesanspro font-12 '>
                                    {defaultToEmptyString(progressInnerItem.submitter)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  )
}

export default ProgressBar
